<template>
  <nav
    class="tab-bar"
    :class="{ hidden: isHidden }"
    aria-label="Main navigation"
  >
    <RouterLink to="/" class="tab-item" :class="{ active: route.path === '/' }">
      <div class="ti">
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
          <path d="M3 9L10 3l7 6v8a1 1 0 01-1 1H4a1 1 0 01-1-1V9z" stroke="currentColor" stroke-width="1.6" stroke-linejoin="round"/>
          <path d="M7 20v-6h6v6" stroke="currentColor" stroke-width="1.6" stroke-linejoin="round"/>
        </svg>
      </div>
      <span class="tl">Home</span>
    </RouterLink>

    <RouterLink to="/challenge" class="tab-item" :class="{ active: route.path.startsWith('/challenge') || route.path.startsWith('/fines') }">
      <div class="ti">
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
          <circle cx="10" cy="10" r="7.5" stroke="currentColor" stroke-width="1.6"/>
          <path d="M10 6.5v7M6.5 10h7" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/>
        </svg>
      </div>
      <span class="tl">New appeal</span>
    </RouterLink>

    <RouterLink to="/history" class="tab-item" :class="{ active: route.path === '/history' }">
      <div class="ti">
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
          <rect x="3" y="3" width="14" height="14" rx="0" stroke="currentColor" stroke-width="1.6"/>
          <path d="M6.5 7h7M6.5 10.5h5M6.5 14h3" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
        </svg>
      </div>
      <span class="tl">My appeals</span>
    </RouterLink>

    <RouterLink to="/faq" class="tab-item" :class="{ active: route.path === '/faq' }">
      <div class="ti">
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
          <circle cx="10" cy="10" r="7.5" stroke="currentColor" stroke-width="1.6"/>
          <path d="M8 8c0-1.1.9-2 2-2s2 .9 2 2c0 1.5-2 2-2 3.2" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
          <circle cx="10" cy="14.5" r=".9" fill="currentColor"/>
        </svg>
      </div>
      <span class="tl">Help</span>
    </RouterLink>

    <RouterLink to="/account" class="tab-item" :class="{ active: route.path === '/account' }">
      <div class="ti">
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
          <circle cx="10" cy="6.5" r="3" stroke="currentColor" stroke-width="1.6"/>
          <path d="M3 18c0-3.9 3.1-6.5 7-6.5s7 2.6 7 6.5" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/>
        </svg>
      </div>
      <span class="tl">Account</span>
    </RouterLink>
  </nav>
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted } from 'vue'
import { useRoute } from 'vue-router'

const route    = useRoute()
const isHidden = ref(false)

let lastScrollY  = 0
let scrollTimer: ReturnType<typeof setTimeout> | null = null

function onScroll() {
  const y     = window.scrollY
  const delta = y - lastScrollY
  if (delta > 8 && y > 60)  isHidden.value = true
  else if (delta < -8)       isHidden.value = false
  lastScrollY = y
  if (scrollTimer) clearTimeout(scrollTimer)
  scrollTimer = setTimeout(() => { isHidden.value = false }, 2000)
}

onMounted(() => {
  lastScrollY = window.scrollY
  window.addEventListener('scroll', onScroll, { passive: true })
})
onUnmounted(() => {
  window.removeEventListener('scroll', onScroll)
  if (scrollTimer) clearTimeout(scrollTimer)
})
</script>

<style scoped>
.tab-bar {
  position: fixed;
  bottom: 0; left: 0; right: 0;
  z-index: 50;
  display: flex;
  align-items: center;
  justify-content: space-around;
  padding: 0 8px calc(12px + var(--safe-bottom));
  background: var(--paper);
  border-top: 1px solid var(--rule);
  height: calc(var(--tab-height) + var(--safe-bottom));
  transition: transform .3s ease;
  will-change: transform;
}
.tab-bar.hidden { transform: translateY(100%); }

.tab-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 3px;
  text-decoration: none;
  flex: 1;
  padding: 6px 4px 0;
  min-height: 44px;
  justify-content: center;
}
.ti {
  width: 32px; height: 32px;
  display: flex; align-items: center; justify-content: center;
  color: var(--ink-faint);
  transition: color .15s;
}
.tl {
  font-family: var(--sans);
  font-size: .65rem;
  font-weight: 500;
  color: var(--ink-faint);
  transition: color .15s;
}
.tab-item.active .ti { color: var(--accent); }
.tab-item.active .tl { color: var(--accent); font-weight: 700; }
</style>
